import { Injectable, Optional } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { UrlAccessService } from 'src/app/features/services/url-access/url-access.servvice';
import { AuthMeStore } from 'src/app/core/auth-me/auth-me.store';
import { canMaintainOutbound } from 'src/app/features/services-integrations/outbound-integrations/outbound-access.util';
import { canViewAuditTrail } from 'src/app/features/adminstrations/admin-audit-trail/audit-trail-access.util';
import {
  isRouteAccessDenied,
  isSelfUserProfileRoute,
  isUserChatLinkRoute,
  resolveRouteAccessPolicyKey,
  UNRESTRICTED_ROUTE_FAMILIES,
} from 'src/app/core/navigation/nav-route-access.util';
import { readAccountKeyFromStorage, resolveGuardAccountKey } from 'src/app/core/navigation/account-key.util';
import { AuthTokenService } from 'src/app/core/auth-me/auth-token.service';
import { resolveAuthMeGuardState } from 'src/app/core/navigation/auth-me-guard-state.util';
import { UserPreferenceDataService } from 'src/app/features/services/users/user_profile/user-preference-data.services';

/** Redirect target, `false` to cancel navigation, or `null` to continue with the guard. */
type GuardRedirect = UrlTree | false | null;

@Injectable({ providedIn: 'root' })
export class AuthGuard  {
  authMe: any;
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly authTokenService: AuthTokenService,
    private readonly urlAccessService: UrlAccessService,
    private readonly authMeStore: AuthMeStore,
    @Optional() private readonly userPreferenceDataService?: UserPreferenceDataService,
  ) { }

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean | UrlTree> {
    const currentUser = this.authService.currentUserValue;
    const hasToken = this.hasUsableToken();
    if (!hasToken) {
      return this.rejectMissingToken(currentUser, state.url);
    }

    const authMeResolution = await resolveAuthMeGuardState(this.authMeStore);
    if (hasToken && !this.authTokenService.get()) {
      return this.logoutUnauthenticated(state.url);
    }

    this.authMe = authMeResolution.authMe;
    const authMeUnavailable = authMeResolution.state === 'unavailable';

    const account = this.getSubdomain();
    const requestedSegments = state.url.split(/[?#]/)[0].split('/').filter(Boolean);
    const requestedAccount = requestedSegments[0] ?? '';

    if (!account) {
      return true;
    }

    // A logged-in visit to "/" has no account in the URL. Land on the saved homepage
    // when the permission landing would otherwise always be Dashboard.
    if (!requestedAccount) {
      const redirect = await this.redirectRootToSavedHomePage(account, currentUser, state.url);
      if (redirect !== null) {
        return redirect;
      }
    }

    const accountRedirect = this.resolveAccountMismatchRedirect(account, requestedAccount, currentUser, state.url);
    if (accountRedirect !== null) {
      return accountRedirect;
    }

    const pageUrl = requestedSegments[1] ?? '';
    if (!pageUrl) {
      // Same landing as "/": a bare /{account} or /{account}/ follows the saved homepage
      // when the permission landing would otherwise always be Dashboard.
      return (await this.redirectRootToSavedHomePage(account, currentUser, state.url)) ?? true;
    }

    return this.resolvePageAccess(route, state, {
      account, currentUser, requestedSegments, pageUrl, authMeUnavailable,
    });
  }

  /**
   * Legacy parity: userstorage.getToken() signs out whenever the token is missing or the
   * string "null" — even if a user object is still cached. Without a cached user the
   * legacy guard also cached the deep link per addLoginFlag before signing out.
   */
  private rejectMissingToken(currentUser: any, requestedUrl: string): false {
    if (currentUser) {
      this.authService.logoutFromAuthGaurd();
      return false;
    }
    return this.logoutUnauthenticated(requestedUrl);
  }

  /** Redirect when the URL account differs from the resolved account or the session's account; null to continue. */
  private resolveAccountMismatchRedirect(
    account: string,
    requestedAccount: string,
    currentUser: any,
    stateUrl: string,
  ): GuardRedirect {
    if (requestedAccount.toLowerCase() !== account.toLowerCase()) {
      const redirect = this.legacyRedirect(account, currentUser, stateUrl);
      if (redirect !== null) {
        return redirect;
      }
    }

    // Legacy parity: another tab signed in to a different account, so the session's
    // stored account no longer matches this tab's URL account. Move this tab to the
    // signed-in account instead of showing one account with another account's token.
    const sessionAccount = readAccountKeyFromStorage();
    if (sessionAccount && requestedAccount.toLowerCase() !== sessionAccount) {
      return this.legacyRedirect(sessionAccount, currentUser, stateUrl);
    }
    return null;
  }

  /** Page-level checks for `/{account}/{page}/...` once the account is settled. */
  private resolvePageAccess(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
    ctx: { account: string; currentUser: any; requestedSegments: string[]; pageUrl: string; authMeUnavailable: boolean },
  ): boolean | UrlTree {
    const { account, currentUser, requestedSegments, pageUrl } = ctx;
    if (pageUrl.length === 1 && this.isOldUrl(state.url)) {
      const redirect = this.getRedirectUrl(state.url, pageUrl);
      return this.router.parseUrl(redirect || '/error/404');
    }

    if (route.routeConfig?.path === '**') {
      return this.router.parseUrl('/error/404');
    }

    const accountRelativeSegments = requestedSegments.slice(1);
    const accountRelativePath = accountRelativeSegments.join('/');
    const selfUserId = this.resolveSelfUserId(currentUser);
    if (isSelfUserProfileRoute(requestedSegments, selfUserId)) {
      return true;
    }

    if (isUserChatLinkRoute(requestedSegments)) {
      return true;
    }

    if (UNRESTRICTED_ROUTE_FAMILIES.has(pageUrl)) {
      return true;
    }

    const policyKey = resolveRouteAccessPolicyKey(accountRelativePath);
    if (!policyKey) {
      return this.router.parseUrl('/error/404');
    }

    // A known route with no initial AuthMe context is unavailable, not denied.
    // Layout retains the URL and suppresses the feature outlet until Retry succeeds.
    if (ctx.authMeUnavailable) {
      return true;
    }

    if (this.isPolicyAccessDenied(policyKey, requestedSegments, accountRelativePath)) {
      return this.denyRoute(account, currentUser, state.url);
    }

    return true;
  }

  private isPolicyAccessDenied(policyKey: string, requestedSegments: string[], accountRelativePath: string): boolean {
    if (
      (policyKey === 'create-outbound' || policyKey === 'outbound-template') &&
      this.isOutboundMaintenanceCreateRoute(['', ...requestedSegments]) &&
      !canMaintainOutbound(this.authMe)
    ) {
      return true;
    }

    if (policyKey === 'audit-trail' && !canViewAuditTrail(this.authMe)) {
      return true;
    }

    return isRouteAccessDenied(accountRelativePath, this.authMe);
  }

  private logoutUnauthenticated(requestedUrl: string): false {
    // Legacy AuthGuard rule: only cache deep-link when addLoginFlag is already 'no'.
    const loginFlag = localStorage.getItem('addLoginFlag');
    if (loginFlag === 'no') {
      localStorage.setItem('addUrlToCache', requestedUrl);
    } else {
      localStorage.setItem('addUrlToCache', '');
    }
    // AuthMe reset happens inside logoutFromAuthGaurd().
    this.authService.logoutFromAuthGaurd();
    return false;
  }

  /** Token present and not the legacy "null" placeholders (userstorage.getToken parity). */
  private hasUsableToken(): boolean {
    const token = this.authTokenService.get();
    return !!token && token !== 'null' && token !== '"null"';
  }

  /**
   * Root URL only. Stakeholder, billing admin, alerts-only, and profile landings stay on
   * getActualRedirectUrl. When that landing is Dashboard, follow the saved homepage
   * (alerts or dashboard). default, empty, and a failed preference read stay on Dashboard.
   */
  private async redirectRootToSavedHomePage(
    accName: string,
    currentUser: any,
    stateUrl: string,
  ): Promise<GuardRedirect> {
    if (!this.authMe) {
      return false;
    }
    let target = this.urlAccessService.getActualRedirectUrl(this.authMe, accName, currentUser);
    if (!target) {
      return null;
    }
    if (target === `/${accName}/dashboard`) {
      const homePage = await this.readSavedHomePage();
      if (homePage === 'alerts' || homePage === 'dashboard') {
        target = `/${accName}/${homePage}`;
      }
    }
    const currentPath = stateUrl.split('?')[0].split('#')[0];
    const targetPath = target.split('?')[0].split('#')[0];
    return currentPath === targetPath ? null : this.router.parseUrl(target);
  }

  /** Preference type 4 home_page. Does not apply theme or write preferences. */
  private async readSavedHomePage(): Promise<string> {
    if (!this.userPreferenceDataService) {
      return '';
    }
    try {
      const ref = await firstValueFrom(this.userPreferenceDataService.ensureLandingPreference());
      const raw = ref?.user_preference_data;
      if (typeof raw !== 'string' || !raw) {
        return '';
      }
      const parsed = JSON.parse(raw) as { home_page?: unknown };
      return typeof parsed?.home_page === 'string' ? parsed.home_page : '';
    } catch {
      return '';
    }
  }

  /**
   * Legacy getActualRedirectUrl + redirectIfNeeded: UrlTree when it changes the URL, null when
   * the target is the current path (navigation continues), false when the permissions lookup
   * is unavailable (the legacy guard's abilities lookup failed and the navigation did not run).
   */
  private legacyRedirect(accName: string, currentUser: any, stateUrl: string): GuardRedirect {
    if (!this.authMe) {
      return false;
    }
    const target = this.urlAccessService.getActualRedirectUrl(this.authMe, accName, currentUser);
    if (!target) {
      return null;
    }
    const currentPath = stateUrl.split('?')[0].split('#')[0];
    const targetPath = target.split('?')[0].split('#')[0];
    return currentPath === targetPath ? null : this.router.parseUrl(target);
  }

  /**
   * Legacy denied-page handling: strip the query string from the address bar, then go back
   * when there is history (window.history.length > 2), otherwise redirect via
   * getActualRedirectUrl.
   */
  private denyRoute(accName: string, currentUser: any, stateUrl: string): boolean | UrlTree {
    const urlWithoutQueryParams = window.location.href.split('?')[0];
    window.history.replaceState({}, document.title, urlWithoutQueryParams);
    // Only go back when the previous entry is an in-app page (the router has already
    // navigated in this tab). On the first navigation — e.g. arriving from the classic UI via
    // "Switch to new UI" (same-tab handoff), a full-page login, or a refresh — history.back()
    // would leave the app (back to the classic UI or the login page), so redirect instead.
    if (window.history.length > 2 && this.router.navigated) {
      window.history.back();
      return true;
    }
    return this.legacyRedirect(accName, currentUser, stateUrl) ?? true;
  }

  private isOutboundMaintenanceCreateRoute(urlHost: string[]): boolean {
    if (urlHost[2] !== 'outbound') {
      return false;
    }
    if (urlHost[3] === 'outbound-template') {
      return true;
    }
    return urlHost[3] === 'edit' && urlHost[5] === 'method' && urlHost[6] === 'new';
  }

  isOldUrl(url: string): boolean {
    return true;
}

getRedirectUrl(url: string, oldURL: string): string | undefined {
  let redirectUrl;
    if(oldURL === 'i') {
      redirectUrl = url.replace('/i/','/business-services/incidents/detail/');
    }
    return redirectUrl;
}
  getSubdomain() {
    return resolveGuardAccountKey(this.router);
  }

  getSubdomainFromUrl() {
    const hostUrl = window.location.pathname;
    const urlHost = hostUrl.split('/');
    let accountKey = "";
    if (urlHost.length > 0) {
      accountKey = urlHost[1];
    }
    return accountKey;
  }

  getToken() {
    return this.authTokenService.get();
  }

  private resolveSelfUserId(currentUser: any): string | number | null | undefined {
    return this.authMe?.userId ?? currentUser?.user_id ?? currentUser?.userId;
  }
}
